import { type ConnectedWheelPickupRecord, WheelPickupOutcome } from '@navio/nsdk';

type BookingWithConnectedPickups = { connectedWheelPickups: ConnectedWheelPickupRecord[] };

type CustomerTimelineOutcome = (typeof WheelPickupOutcome)['PLANNED'] | (typeof WheelPickupOutcome)['COLLECTED'];

/** Latest attempt the customer timeline can describe. Failed, partial, and cancelled attempts are not in this set. */
export type CustomerTimelineWheelPickup = ConnectedWheelPickupRecord & {
  outcome: CustomerTimelineOutcome;
};

const isCustomerTimelineOutcome = (outcome: WheelPickupOutcome): outcome is CustomerTimelineOutcome =>
  outcome === WheelPickupOutcome.PLANNED || outcome === WheelPickupOutcome.COLLECTED;

/** The visit did not hand us the wheels: nothing collected, or only part of the set. */
const isFailedPickupOutcome = (outcome: WheelPickupOutcome): boolean =>
  outcome === WheelPickupOutcome.FAILED || outcome === WheelPickupOutcome.PARTIAL;

const latestWheelPickups = (booking: BookingWithConnectedPickups): ConnectedWheelPickupRecord[] =>
  booking.connectedWheelPickups.filter((pickup) => pickup.isLatestAttempt);

/** Planned and collected attempts only. A failed competitor pickup stays in the partner app. */
export const customerTimelineWheelPickups = (booking: BookingWithConnectedPickups): CustomerTimelineWheelPickup[] =>
  latestWheelPickups(booking).filter((pickup): pickup is CustomerTimelineWheelPickup =>
    isCustomerTimelineOutcome(pickup.outcome)
  );

/**
 * Failed pickups still awaiting a resolution — one per car at most. A car whose newer attempt
 * succeeded is not in recovery, so its older failures are history rather than something to act on.
 */
export const activeFailedWheelPickups = (booking: BookingWithConnectedPickups): ConnectedWheelPickupRecord[] =>
  latestWheelPickups(booking).filter((pickup) => isFailedPickupOutcome(pickup.outcome));

/** A failed attempt that never received the wheels can be booked again. A partial receipt cannot. */
export const canReplacePickup = (pickup: ConnectedWheelPickupRecord): boolean =>
  pickup.outcome === WheelPickupOutcome.FAILED && !pickup.wheelsReceived;
